<template>
    <div class="pb-8 sm:pb-12">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="mx-auto max-w-4xl text-center">
                <p class="text-balance text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl dark:text-white">Compare our plans and find yours</p>
            </div>
            <p class="mx-auto mt-2 max-w-2xl text-pretty text-center text-lg font-medium text-gray-600 sm:text-xl/8 dark:text-gray-400">
                Simple, transparent pricing that grows with you.
            </p>

            <p v-if="subscribeError" class="mx-auto mt-4 max-w-2xl rounded-md bg-red-50 px-4 py-2 text-center text-sm text-red-700" role="alert">
                {{ subscribeError }}
            </p>

            <!-- Mobile (< lg): stacked per-tier cards. The comparison table below is hidden on mobile so
                 users no longer have to horizontally scroll a hidden set of columns. -->
            <div class="mt-6 space-y-6 lg:hidden">
                <div
                    v-for="tier in tiers"
                    :key="'m-' + tier.id"
                    class="rounded-2xl border bg-white p-6 dark:bg-gray-900"
                    :class="tier.mostPopular ? 'border-yellow-500 ring-1 ring-yellow-500' : 'border-gray-200 dark:border-gray-700'"
                >
                    <div class="flex items-center justify-center gap-2">
                        <ProTierIcon v-if="tier.planSlug === 'protect-pro'" />
                        <h3 class="text-2xl font-semibold text-[#323c61] dark:text-white">{{ tier.name }}</h3>
                    </div>
                    <div class="text-center">
                        <ToolTip trigger="click" title="Starting at">
                            <template #trigger>
                                <span class="text-xs font-medium text-[#323c61] underline cursor-pointer dark:text-gray-400">Starting at</span>
                            </template>
                            <StartingAtPopupTable :rows="startingAtRows" show-title>
                                <div v-if="proCtaFor(proTier)" class="mt-3 text-center">
                                    <button type="button" class="rounded-md bg-[#2f3a5e] px-6 py-2 text-sm font-bold text-white hover:opacity-90" @click="runCta(proTier)">
                                        {{ proCtaFor(proTier).label }}
                                    </button>
                                </div>
                            </StartingAtPopupTable>
                        </ToolTip>
                    </div>

                    <div class="mt-3">
                        <div class="flex items-baseline justify-center gap-x-1 text-[#323c61] dark:text-white">
                            <span class="text-4xl font-semibold">{{ displayPrice(tier) }}</span>
                            <span class="text-4xl font-semibold">{{ displayPriceSuffix(tier) }}</span>
                        </div>
                        <!-- Reserved (invisible on Basic) so the cards line up. -->
                        <p class="mt-1 text-sm italic text-center text-[#323c61] dark:text-gray-200" :class="tier.billedAnnually ? '' : 'invisible'">Billed Annually at {{ tier.billedAnnually || '$0' }}</p>
                        <p v-if="tier.description" class="mt-2 text-sm text-[#323c61] dark:text-gray-400"><span class="font-bold">Best for</span>: {{ tier.description }}</p>
                    </div>

                    <div class="mt-5">
                        <template v-if="ctaFor(tier)">
                                        <inertia-link v-if="ctaFor(tier).href" :href="ctaFor(tier).href" class="block w-full rounded-md px-3 py-2 text-center text-base font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed" :class="ctaClass(ctaFor(tier))">{{ ctaFor(tier).label }}</inertia-link>
                                        <button v-else type="button" :disabled="ctaFor(tier).disabled || isSubscribing" class="block w-full rounded-md px-3 py-2 text-center text-base font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed" :class="ctaClass(ctaFor(tier))" @click="runCta(tier)">{{ isSubscribing && ctaFor(tier).kind === 'upgrade' ? 'Processing...' : ctaFor(tier).label }}</button>
                                    </template>
                    </div>

                    <div class="mt-6 space-y-4">
                        <div v-for="entry in sectionsWithIncluded(tier)" :key="'m-' + tier.id + '-' + entry.section.name">
                            <div class="flex items-center gap-1.5">
                                <p class="text-sm font-semibold text-yellow-600 dark:text-yellow-500">{{ entry.section.name }}</p>
                                <span v-if="entry.section.comingSoon" class="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400">Coming Soon</span>
                            </div>
                            <ul role="list" class="mt-2 space-y-2">
                                <li
                                    v-for="feature in entry.features"
                                    :key="feature.name"
                                    class="flex items-start gap-x-2 text-sm"
                                    :class="entry.section.comingSoon ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'"
                                >
                                    <font-awesome-icon :icon="faCheck" class="mt-0.5 h-3.5 w-3.5 flex-none" :class="entry.section.comingSoon ? 'text-gray-300 dark:text-gray-600' : 'text-green-500'" aria-hidden="true" />
                                    <span>{{ feature.name }}{{ featureValueSuffix(feature, tier) }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Desktop (lg+): full comparison table. Hidden on mobile in favor of the cards above. -->
            <div class="hidden lg:block isolate mt-4">
                <div class="relative -mx-4 overflow-x-auto lg:-mx-8 lg:overflow-visible">
                    <table class="w-full min-w-[800px] table-fixed border-collapse text-left lg:min-w-0">
                        <caption class="sr-only">
                            Pricing plan comparison
                        </caption>
                        <colgroup>
                            <col :style="{ width: columnWidth }" />
                            <col v-for="tier in tiers" :key="'col-' + tier.id" :style="{ width: columnWidth }" />
                        </colgroup>
                        <thead>
                            <tr>
                                <td></td>
                                <th v-for="tier in tiers" :key="tier.id" scope="col" class="px-3 pt-4 text-xs lg:px-6 lg:pt-6 xl:px-8 xl:pt-8">
                                    <div class="flex items-center justify-center gap-1.5 lg:gap-2">
                                        <ProTierIcon v-if="tier.planSlug === 'protect-pro'" />
                                        <div class="text-lg font-semibold text-[#323c61] dark:text-white lg:text-2xl">{{ tier.name }}</div>
                                    </div>
                                    <div class="text-center">
                                        <ToolTip trigger="click" title="Starting at">
                                            <template #trigger>
                                                <span class="text-xs font-medium text-[#323c61] underline cursor-pointer dark:text-gray-400">Starting at</span>
                                            </template>
                                            <StartingAtPopupTable :rows="startingAtRows" show-title>
                                                <div v-if="proCtaFor(proTier)" class="mt-3 text-center">
                                                    <button type="button" class="rounded-md bg-[#2f3a5e] px-6 py-2 text-sm font-bold text-white hover:opacity-90" @click="runCta(proTier)">
                                                        {{ proCtaFor(proTier).label }}
                                                    </button>
                                                </div>
                                            </StartingAtPopupTable>
                                        </ToolTip>
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th scope="row"><span class="sr-only">Price</span></th>
                                <td v-for="tier in tiers" :key="tier.id" class="px-3 pt-2 align-top lg:px-6 xl:px-8">
                                    <div class="flex min-h-[10rem] flex-col sm:min-h-[11rem] lg:min-h-[12rem]">
                                        <div>
                                            <div class="flex items-baseline justify-center gap-x-1 text-[#323c61] dark:text-white">
                                                <span class="text-2xl font-semibold lg:text-4xl">{{ displayPrice(tier) }}</span>
                                                <span class="text-2xl font-semibold lg:text-4xl">{{ displayPriceSuffix(tier) }}</span>
                                            </div>
                                            <!-- Always rendered (invisible on Basic) so descriptions align across columns. -->
                                            <p class="mt-1 text-sm italic text-center text-[#323c61] dark:text-gray-200 lg:mt-2 lg:text-base" :class="tier.billedAnnually ? '' : 'invisible'">Billed Annually at {{ tier.billedAnnually || '$0' }}</p>
                                            <p v-if="tier.description" class="mt-1.5 text-sm text-[#323c61] dark:text-gray-400 lg:mt-2 lg:text-base"><span class="font-bold">Best for</span>: {{ tier.description }}</p>
                                        </div>
                                        <div class="mt-auto pt-4 lg:pt-8">
                                    <template v-if="ctaFor(tier)">
                                        <inertia-link v-if="ctaFor(tier).href" :href="ctaFor(tier).href" class="block w-full rounded-md px-2 py-1.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed lg:px-3 lg:py-2 lg:text-base" :class="ctaClass(ctaFor(tier))">{{ ctaFor(tier).label }}</inertia-link>
                                        <button v-else type="button" :disabled="ctaFor(tier).disabled || isSubscribing" class="block w-full rounded-md px-2 py-1.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed lg:px-3 lg:py-2 lg:text-base" :class="ctaClass(ctaFor(tier))" @click="runCta(tier)">{{ isSubscribing && ctaFor(tier).kind === 'upgrade' ? 'Processing...' : ctaFor(tier).label }}</button>
                                    </template>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <template v-for="(entry, sectionIdx) in displaySections" :key="entry.section.name">
                                <tr v-if="sectionIdx > 0" class="h-2 lg:h-4">
                                    <td :colspan="totalColumns"></td>
                                </tr>
                                <tr>
                                    <th scope="colgroup" :colspan="totalColumns" :class="[sectionIdx === 0 ? 'pt-6 pb-2 lg:pt-12 lg:pb-4' : 'pt-2 pb-2 lg:pt-4 lg:pb-4', 'text-xs font-semibold text-yellow-600 dark:text-yellow-500 lg:text-sm']">
                                        <div class="flex items-center gap-1.5 lg:gap-2">
                                            <span>{{ entry.section.name }}</span>
                                            <span v-if="entry.section.comingSoon" class="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-400 lg:px-2.5 lg:text-sm">Coming Soon</span>
                                        </div>
                                        <div class="absolute inset-x-4 mt-2 h-px bg-gray-900/10 dark:bg-white/10 lg:inset-x-8 lg:mt-4"></div>
                                    </th>
                                </tr>
                                <tr v-for="(feature, featureIdx) in entry.features" :key="feature.name" :class="[(entry.startIndex + featureIdx) % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/50', entry.section.comingSoon ? 'opacity-50' : '']">
                                    <th scope="row" class="py-1.5 pl-3 pr-4 text-xs font-normal lg:py-3 lg:pl-6 lg:pr-8 lg:text-sm" :class="entry.section.comingSoon ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-gray-200'">
                                        <div class="flex items-center gap-1.5 lg:gap-2">
                                            <span class="leading-tight">{{ feature.name }}</span>
                                            <ToolTip
                                                v-if="feature.hasInfo && feature.infoText"
                                                :title="feature.name"
                                                :description="feature.infoText"
                                            />
                                        </div>
                                        <div class="absolute inset-x-4 mt-2 h-px bg-gray-900/5 dark:bg-white/5 lg:inset-x-8 lg:mt-4"></div>
                                    </th>
                                    <td v-for="tier in tiers" :key="tier.id" class="px-3 py-1.5 lg:px-6 lg:py-3 xl:px-8">
                                        <div v-if="typeof feature.tiers[tierKey(tier)] === 'string'" class="text-center text-base leading-tight" :class="entry.section.comingSoon ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'">{{ feature.tiers[tierKey(tier)] }}</div>
                                        <template v-else>
                                            <div v-if="feature.tiers[tierKey(tier)] === true" class="flex justify-center">
                                                <div class="flex h-4 w-4 items-center justify-center rounded-full border-2 bg-white dark:bg-gray-800 lg:h-5 lg:w-5" :class="entry.section.comingSoon ? 'border-gray-300 dark:border-gray-600' : 'border-green-500'">
                                                    <font-awesome-icon :icon="faCheck" :class="entry.section.comingSoon ? 'h-2.5 w-2.5 text-gray-300 dark:text-gray-600 lg:h-3 lg:w-3' : 'h-2.5 w-2.5 text-green-500 lg:h-3 lg:w-3'" aria-hidden="true" />
                                                </div>
                                            </div>
                                            <div v-else class="flex justify-center">
                                                <font-awesome-icon :icon="faMinus" :class="entry.section.comingSoon ? 'h-3 w-6 text-gray-300 dark:text-gray-600 lg:h-4 lg:w-8' : 'h-3 w-6 text-gray-400 dark:text-gray-500 lg:h-4 lg:w-8'" aria-hidden="true" />
                                            </div>
                                            <span class="sr-only">{{ feature.tiers[tierKey(tier)] === true ? 'Included' : 'Not included' }} in {{ tier.name }}</span>
                                        </template>
                                    </td>
                                </tr>
                                <tr v-if="entry.hasMore">
                                    <td :colspan="totalColumns" class="py-1.5 pl-3 lg:py-2 lg:pl-6">
                                        <button
                                            type="button"
                                            class="flex items-center gap-1.5 text-xs font-medium text-blue-600 underline hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 lg:text-sm"
                                            :aria-expanded="entry.expanded"
                                            @click="toggleSection(entry.section.name)"
                                        >
                                            <span>{{ entry.expanded ? 'Collapse Features' : 'Expand all Features' }}</span>
                                            <font-awesome-icon :icon="faChevronDown" class="h-3 w-3 transition-transform" :class="entry.expanded ? 'rotate-180' : ''" aria-hidden="true" />
                                        </button>
                                    </td>
                                </tr>
                            </template>
                            <!-- CTA buttons row (same layout as price row, below all sections) -->
                            <tr>
                                <td class="px-3 pt-4 lg:px-6 xl:px-8"></td>
                                <td v-for="tier in tiers" :key="'cta-' + tier.id" class="px-3 pt-4 lg:px-6 xl:px-8">
                                    <template v-if="ctaFor(tier)">
                                        <inertia-link v-if="ctaFor(tier).href" :href="ctaFor(tier).href" class="mt-4 block w-full rounded-md px-2 py-1.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed lg:mt-8 lg:px-3 lg:py-2 lg:text-base" :class="ctaClass(ctaFor(tier))">{{ ctaFor(tier).label }}</inertia-link>
                                        <button v-else type="button" :disabled="ctaFor(tier).disabled || isSubscribing" class="mt-4 block w-full rounded-md px-2 py-1.5 text-center text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed lg:mt-8 lg:px-3 lg:py-2 lg:text-base" :class="ctaClass(ctaFor(tier))" @click="runCta(tier)">{{ isSubscribing && ctaFor(tier).kind === 'upgrade' ? 'Processing...' : ctaFor(tier).label }}</button>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            
            <!-- RAC-10417: Enterprise option. Enterprise signup moved off the
                 registration form; the pricing page is now its entry point.
                 The CTA is role-aware because /pricing renders for guests and
                 authenticated users alike — see enterpriseCta below. -->
            <div v-if="enterpriseRegistrationEnabled" class="mt-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900 lg:mt-10">
                <div class="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Enterprise</h3>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            {{ enterpriseCta.description }}
                        </p>
                    </div>
                    <a
                        v-if="enterpriseCta.isExternal"
                        :href="enterpriseCta.href"
                        class="w-full shrink-0 rounded-md bg-yellow-500 px-4 py-2 text-center text-base font-semibold text-white hover:bg-yellow-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-400 lg:w-auto"
                    >
                        {{ enterpriseCta.label }}
                    </a>
                    <inertia-link
                        v-else
                        :href="enterpriseCta.href"
                        class="w-full shrink-0 rounded-md bg-yellow-500 px-4 py-2 text-center text-base font-semibold text-white hover:bg-yellow-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-400 lg:w-auto"
                    >
                        {{ enterpriseCta.label }}
                    </inertia-link>
                </div>
            </div>

            <!-- Subscription Management Note -->
            <div v-if="pricingState !== 'basic'" class="mt-8 text-center">
                <p class="text-sm text-gray-600 dark:text-gray-400">
                    Want to manage your subscription? 
                    <inertia-link :href="route('payment-methods.index')" class="text-yellow-600 hover:text-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-400 underline">
                        Click here
                    </inertia-link>
                </p>
            </div>
        </div>

        <!-- 86bcd9hg3: the Pro pop-up (price shown before anything is charged) and, for
             landlords on an earlier plan, "Switch to new plan". -->
        <RiskGuardProUpsellModal
            :show="showUpsell"
            :processing="isSubscribing"
            :pro-plan="proPlan"
            :stripe-data="canSubscribe ? stripeData : null"
            :user-payment-method="paymentMethod"
            :subscription="subscriptionProtection"
            :show-features="false"
            :notice="tierChangedNotice"
            @subscribe="handleSubscribeStart"
            @dismiss="closeUpsell"
        />
        <SwitchToNewPlanModal
            :show="showSwitch"
            :stripe-data="canSubscribe ? stripeData : null"
            @close="showSwitch = false"
            @switched="showSwitch = false"
        />

        <!-- Subscription Protection Component -->
        <!-- Show for both authenticated and session-based users (if stripeData is available) -->
        <subscription-protection-v2
            v-if="hasUser && stripeData && stripeData.publishable_key"
            ref="subscriptionProtection"
            :subscription-type="'protect'"
            :terms-accepted="termsAccepted"
            :has-payment-method="hasPaymentMethod"
            :user-payment-method="paymentMethod"
            :stripe-data="stripeData"
            :plan-slug="selectedPlanSlug"
            :billing-cycle="'annual'"
            :show-confirmation-modal="true"
            :success-redirect-url="isOnboarding ? route('agent.dashboard') : null"
            @success="handleSubscriptionSuccess"
            @error="handleSubscriptionError"
            @cancel="handleSubscriptionCancel"
            @tier-changed="handleTierChanged"
            @restore-plan-selection="restorePendingSubscription"
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCheck, faMinus, faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import ToolTip from '@/Shared/ToolTip.vue'
import ProTierIcon from '@/Pages/V2/Properties/Components/ProTierIcon.vue'
import StartingAtPopupTable from '@/Pages/V2/Properties/Components/StartingAtPopupTable.vue'
import { buildStartingAtRows } from '@/Pages/V2/Properties/startingAtPopup'
import { resolveEnterpriseCta } from '@/Pages/V2/Properties/enterpriseCta'
import SubscriptionProtectionV2 from '@/Shared/V2/SubscriptionProtection.vue'
import RiskGuardProUpsellModal from '@/Components/RiskGuardProUpsellModal.vue'
import SwitchToNewPlanModal from '@/Components/SwitchToNewPlanModal.vue'
import { proCta } from '@/Shared/proPlanState'

const page = usePage()
const isAuthenticated = computed(() => !!page.props.auth?.user)

/**
 * RAC-10417 — the Enterprise band.
 *
 * The band is hidden entirely when enterprise registration is off, because
 * GetStartedController rejects ?role=enterprise in that state with
 * "Enterprise registration is temporarily unavailable" — advertising a CTA
 * that is guaranteed to bounce is worse than not advertising it.
 *
 * CTA resolution lives in enterpriseCta.js so it can be unit tested; see the
 * comment there for why it has to be role-aware.
 */
const enterpriseRegistrationEnabled = computed(() => !!page.props.enterprise_registration_enabled)

const enterpriseCta = computed(() => resolveEnterpriseCta(page.props.auth?.user, {
    intake: route('enterprise.intake.show'),
    getStarted: route('get-started') + '?role=enterprise',
}))

const props = defineProps({
    tiers: {
        type: Array,
        required: true
    },
    sections: {
        type: Array,
        required: true
    },
    stripeData: {
        type: [Object, Array],
        default: null
    },
    paymentMethod: {
        type: Object,
        default: null
    },
    termsAccepted: {
        type: Boolean,
        default: false
    },
    hasPaymentMethod: {
        type: Boolean,
        default: false
    },
    isOnboarding: {
        type: Boolean,
        default: false
    },
    // 86bcd9hg3: account-wide Pro. 'basic' | 'legacy' | 'pro' for the landlord (visitors
    // are 'basic'); proPlan is their pricing summary with the tier quote; proTiers the
    // tier table, lowest first.
    pricingState: {
        type: String,
        default: 'basic'
    },
    proPlan: {
        type: Object,
        default: null
    },
    proTiers: {
        type: Array,
        default: () => []
    },
})

const emit = defineEmits(['error', 'success'])

// Feature-matrix cells are matched on each plan's stable base slug, never its
// mutable DB display name. Basic carries no plan (planSlug === null) so it keys
// to 'basic'. This is why renaming a plan can no longer blank out its column.
const tierKey = (tier) => tier?.planSlug ?? 'basic'

// The comparison table is one label column plus one column per tier. Deriving the
// count (and each column's width) keeps the grid balanced when a tier is hidden.
const totalColumns = computed(() => props.tiers.length + 1)
const columnWidth = computed(() => `${100 / totalColumns.value}%`)

// --- Price/feature presentation shared by the desktop table and the mobile cards ---

// 86bcd9hg3 (decision 23): every price is a monthly figure — Basic's $0 and Pro's lowest
// tier as its monthly equivalent, "Billed Annually at $199" underneath.
const displayPrice = (tier) => tier.startingAt
const displayPriceSuffix = () => '/mo'

// The "Starting at" tier table: Units Managed | Basic (free) | Pro per month.
const startingAtRows = computed(() => buildStartingAtRows(props.proTiers))
const proTier = computed(() => props.tiers.find((tier) => tier.planSlug === 'protect-pro') ?? null)

// Mobile cards list only what a tier includes. A feature is included when its
// value is true or a string ('$', '$49', '1'); false/absent features are omitted.
const includedFeatures = (section, tier) => {
    const key = tierKey(tier)
    return section.features.filter((feature) => feature.tiers[key] === true || typeof feature.tiers[key] === 'string')
}

// Inline suffix for a string-valued feature on a card, e.g. "Report Views (1)".
const featureValueSuffix = (feature, tier) => {
    const value = feature.tiers[tierKey(tier)]
    return typeof value === 'string' ? ` (${value})` : ''
}

// Sections that have at least one included feature for this tier (skip empty groups on cards).
const sectionsWithIncluded = (tier) => props.sections
    .map((section) => ({ section, features: includedFeatures(section, tier) }))
    .filter((entry) => entry.features.length > 0)

// Desktop table sections collapse to their top rows with a per-section expand
// toggle (RAC-10438). startIndex is the running count of VISIBLE rows above each
// section so zebra striping stays alternating when rows are hidden.
const COLLAPSED_ROW_COUNT = 4
const expandedSections = ref({})

const toggleSection = (sectionName) => {
    expandedSections.value = {
        ...expandedSections.value,
        [sectionName]: !expandedSections.value[sectionName],
    }
}

const displaySections = computed(() => {
    let runningIndex = 0

    return props.sections.map((section) => {
        const expanded = !!expandedSections.value[section.name]
        const features = expanded ? section.features : section.features.slice(0, COLLAPSED_ROW_COUNT)
        const entry = {
            section,
            features,
            expanded,
            hasMore: section.features.length > COLLAPSED_ROW_COUNT,
            startIndex: runningIndex,
        }
        runningIndex += features.length

        return entry
    })
})

const isSubscribing = ref(false)
const selectedPlanSlug = ref(null)
const subscriptionProtection = ref(null)
const showUpsell = ref(false)
const showSwitch = ref(false)
const subscribeError = ref('')

// Subscribing needs card-entry data, which the backend only sends when there is a user
// (signed in, or a pending sign-up). Visitors are sent to sign up instead.
const canSubscribe = computed(() => {
    if (!props.stripeData || typeof props.stripeData !== 'object' || Array.isArray(props.stripeData)) {
        return false
    }
    return !!(props.stripeData.publishable_key && typeof props.stripeData.publishable_key === 'string')
})

const hasUser = computed(() => isAuthenticated.value || canSubscribe.value)

/**
 * The button each plan column shows (86bcd9hg3, shared rule in proPlanState.js):
 * Basic "Try Risk-Free"; Pro "Get Started" (upgrade pop-up), "Switch to new plan" for
 * landlords on an earlier plan, "Current Plan" once on Pro, "Update card" after a
 * failed payment. Visitors without an account sign up first.
 */
const ctaFor = (tier) => {
    if (!tier.planSlug) {
        if (props.pricingState === 'pro') {
            return null
        }

        return props.pricingState === 'legacy' ? null : { kind: 'free', label: 'Try Risk-Free' }
    }

    return proCtaFor(tier)
}

const proCtaFor = (tier) => {
    if (!tier) {
        return null
    }

    if (!hasUser.value) {
        return { kind: 'sign-up', label: 'Get Started', href: route('get-started') }
    }

    switch (proCta(props.proPlan ?? { state: props.pricingState })) {
        case 'upgrade':
            return { kind: 'upgrade', label: 'Get Started', disabled: !canSubscribe.value }
        case 'switch':
            return { kind: 'switch', label: 'Switch to new plan' }
        case 'fix-card':
            return { kind: 'fix-card', label: 'Update card', href: route('payment-methods.index') }
        default:
            return { kind: 'current', label: 'Current Plan', disabled: true }
    }
}

const ctaClass = (cta) => cta?.kind === 'current'
    ? 'bg-gray-200 text-gray-600 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'
    : 'bg-yellow-500 text-white hover:bg-yellow-400 focus-visible:outline-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-400'

const runCta = (tier) => {
    const cta = tier?.planSlug ? proCtaFor(tier) : ctaFor(tier)

    if (!cta || cta.disabled) {
        return
    }

    subscribeError.value = ''

    if (cta.kind === 'free') {
        handleFreePlanClick()
    } else if (cta.kind === 'upgrade') {
        showUpsell.value = true
    } else if (cta.kind === 'switch') {
        showSwitch.value = true
    }
}

// Restore pending subscription from sessionStorage (e.g. after adding a card).
const restorePendingSubscription = () => {
    try {
        const stored = sessionStorage.getItem('pending_subscription_plan')
        const planData = stored ? JSON.parse(stored) : null

        if (planData && typeof planData === 'object') {
            selectedPlanSlug.value = planData.planSlug
        }
    } catch {
        try {
            sessionStorage.removeItem('pending_subscription_plan')
        } catch {
            // Ignore clear errors
        }
    }
}

onMounted(() => {
    restorePendingSubscription()

    // ?upgrade=1 (top bar, rent payments / maintenance pages) opens the Pro pop-up.
    try {
        const wantsUpgrade = new URLSearchParams(window.location.search).get('upgrade') === '1'
        if (wantsUpgrade && props.pricingState === 'basic' && canSubscribe.value) {
            showUpsell.value = true
        }
    } catch {
        // No window (SSR) — nothing to open.
    }
})

const handleFreePlanClick = () => {
    // 86bc2cgxd: new landlords picking Basic straight after signup start on
    // the Home tab; everyone else keeps landing on their properties.
    isSubscribing.value = true
    router.visit(route(props.isOnboarding ? 'agent.dashboard' : 'properties.index'))
}

// Why the pop-up reopened (the price moved before the charge); shown inside it.
const tierChangedNotice = ref(null)

const closeUpsell = () => {
    showUpsell.value = false
    tierChangedNotice.value = null
}

// 86bc9e403: the pop-up is the combined in-place checkout — it stores the plan, saves a card
// if needed and subscribes itself through the SubscriptionProtection ref. We only mirror the
// plan slug into the prop (a fallback for the subscribe request) and flag the in-flight state.
const handleSubscribeStart = () => {
    selectedPlanSlug.value = 'protect-pro-tiered'
    subscribeError.value = ''
    isSubscribing.value = true
}

const handleSubscriptionSuccess = (data = {}) => {
    isSubscribing.value = false
    selectedPlanSlug.value = null
    // 86bc9e403: the combined checkout stays open through the subscribe, so close it on success.
    showUpsell.value = false

    try {
        sessionStorage.removeItem('pending_subscription_plan')
    } catch {
        // Silently fail
    }

    // 86bc2cgxd: straight after signup, land the user on the Home tab.
    // 86bc9e403: outside that flow the Pro checkout is in-place — stay on /pricing (the
    // SubscriptionProtection already reloaded it) unless the backend returned a redirect.
    const redirectUrl = props.isOnboarding
        ? route('agent.dashboard')
        : (data?.redirectUrl || null)

    if (redirectUrl) {
        router.visit(redirectUrl)
    }
}

const handleSubscriptionError = (error) => {
    isSubscribing.value = false
    const message = error?.error || 'We could not start your subscription. Please try again.'
    subscribeError.value = message
    // 86bc9e403 #1: also surface it inside the still-open pop-up (its notice), so the re-enabled
    // Subscribe button sits next to the reason rather than behind the modal backdrop.
    tierChangedNotice.value = message
}

// The price moved since the pop-up (a property was added): reload the quote and show
// the pop-up again with the new price. Nothing was charged.
const handleTierChanged = ({ message }) => {
    isSubscribing.value = false
    router.reload({
        only: ['proPlan', 'auth'],
        onSuccess: () => {
            tierChangedNotice.value = message || null
            showUpsell.value = true
        },
    })
}

const handleSubscriptionCancel = () => {
    isSubscribing.value = false
    selectedPlanSlug.value = null

    try {
        sessionStorage.removeItem('pending_subscription_plan')
    } catch {
        // Silently fail
    }
}
</script>
